import * as Sentry from '@sentry/browser';
import React from 'react';
import {FormattedMessage, type IntlShape} from 'react-intl';
import {Detail, FlexRow, InlineText, WorkSignifier} from '@forecast-it/design-system';
import {
	AGENT_STARTED_BY,
	type AgentNotificationContent,
	type AgentNotifiedResult,
	type AgentRecord,
	type AgentRow,
	type AgentStartedBy,
} from './agentDeclaration';
import {getAgent} from './agentRegistry';
import {AGENTIC_WORKERS_MESSAGE, describeStartedBy} from './agenticWorkersMessages';

export const AGENT_NOTIFICATION_ACTION = 'AGENTIC_WORKER_RUN_PRODUCED';
export const AGENT_NOTIFICATION_ENTITY_TYPE = 'AGENTIC_WORKER_RUN';

export const AGENT_NOTIFICATION_PARAMS_KEY = {
	AGENT_ID: 'agentId',
	STARTED_BY_KIND: 'startedByKind',
	RESULTS: 'results',
	CONTENT: 'content',
} as const;

const RESULT_KEY = {
	RESULT_ID: 'resultId',
	ROWS: 'rows',
} as const;

const ROW_KEY = {
	ROW_TYPE: 'rowType',
	ROW_ID: 'rowId',
} as const;

const PLUS_MORE_MESSAGE = 'common.plus-more';
const OTHER_RECORDS_SEPARATOR = ', ';
const UNREADABLE_PARAMS_REPORT = 'An agent notification carried params this browser could not read';
const UNREADABLE_AGENT_ANSWER_REPORT = 'An agent described its notification in a shape this browser could not draw';

const reportedParams = new Set<string>();

type StoredAgentResult = {resultId: string; rows: AgentRow[]};

export type AgentNotificationRow = {
	agentName: string | null;
	text: string;
	records: AgentRecord[];
	startedByText: string | null;
	path: string | null;
	title: string;
};

export const isAgentNotification = (publisherAction: string | null | undefined): boolean =>
	publisherAction === AGENT_NOTIFICATION_ACTION;

const isObject = (candidate: unknown): candidate is Record<string, unknown> =>
	typeof candidate === 'object' && candidate !== null && !Array.isArray(candidate);

const isStartedBy = (candidate: unknown): candidate is AgentStartedBy =>
	candidate === AGENT_STARTED_BY.MANUAL || candidate === AGENT_STARTED_BY.SCHEDULE;

const isAgentRow = (candidate: unknown): candidate is AgentRow =>
	isObject(candidate) && typeof candidate[ROW_KEY.ROW_TYPE] === 'string' && typeof candidate[ROW_KEY.ROW_ID] === 'number';

const isAgentRows = (candidate: unknown): candidate is AgentRow[] => Array.isArray(candidate) && candidate.every(isAgentRow);

const isAgentResult = (candidate: unknown): candidate is StoredAgentResult =>
	isObject(candidate) && typeof candidate[RESULT_KEY.RESULT_ID] === 'string' && isAgentRows(candidate[RESULT_KEY.ROWS]);

const isAgentResults = (candidate: unknown): candidate is StoredAgentResult[] =>
	Array.isArray(candidate) && candidate.length > 0 && candidate.every(isAgentResult);

const isAgentRecord = (candidate: unknown): candidate is AgentRecord =>
	isObject(candidate) &&
	typeof candidate.label === 'string' &&
	typeof candidate.name === 'string' &&
	typeof candidate.color === 'string';

const isAgentNotificationContent = (candidate: unknown): candidate is AgentNotificationContent =>
	isObject(candidate) &&
	typeof candidate.text === 'string' &&
	Array.isArray(candidate.records) &&
	candidate.records.every(isAgentRecord);

const parseParams = (params: string): Record<string, unknown> | null => {
	try {
		const parsed: unknown = JSON.parse(params);
		return isObject(parsed) ? parsed : null;
	} catch {
		return null;
	}
};

// The bell draws its rows again each time it opens, so each notification it cannot draw is reported once per page load.
const reportOnce = (params: string, report: () => void): void => {
	if (reportedParams.has(params)) {
		return;
	}
	reportedParams.add(params);
	report();
};

export const clearReportedAgentNotifications = (): void => {
	reportedParams.clear();
};

const describeFallback = (startedByText: string | null, intl: IntlShape): AgentNotificationRow => {
	const text = intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.NOTIFICATION_UNKNOWN_AGENT});
	return {
		agentName: null,
		text,
		records: [],
		startedByText,
		path: null,
		title: text,
	};
};

// The bell renders on every page, so nothing in here may throw: whatever the params hold, a row comes back.
export const describeAgentNotification = (params: string, intl: IntlShape): AgentNotificationRow => {
	const parsed = parseParams(params);
	if (parsed === null) {
		reportOnce(params, () => Sentry.captureMessage(UNREADABLE_PARAMS_REPORT, 'warning'));
		return describeFallback(null, intl);
	}
	const startedByKind = parsed[AGENT_NOTIFICATION_PARAMS_KEY.STARTED_BY_KIND];
	const startedByText = isStartedBy(startedByKind) ? describeStartedBy(startedByKind, null, intl) : null;
	const agentId = parsed[AGENT_NOTIFICATION_PARAMS_KEY.AGENT_ID];
	const storedResults = parsed[AGENT_NOTIFICATION_PARAMS_KEY.RESULTS];
	if (typeof agentId !== 'string' || !isAgentResults(storedResults) || startedByText === null) {
		reportOnce(params, () => Sentry.captureMessage(UNREADABLE_PARAMS_REPORT, 'warning'));
		return describeFallback(startedByText, intl);
	}
	const declaration = getAgent(agentId);
	if (declaration === undefined) {
		return describeFallback(startedByText, intl);
	}
	try {
		const results: AgentNotifiedResult[] = storedResults.map(result => ({
			id: result.resultId,
			rows: result.rows,
		}));
		const content: unknown = declaration.describeNotification(
			{results, content: parsed[AGENT_NOTIFICATION_PARAMS_KEY.CONTENT]},
			intl
		);
		if (!isAgentNotificationContent(content)) {
			reportOnce(params, () => Sentry.captureMessage(UNREADABLE_AGENT_ANSWER_REPORT, 'warning'));
			return describeFallback(startedByText, intl);
		}
		const {text, records} = content;
		const agentName = intl.formatMessage(declaration.name);
		return {
			agentName,
			text,
			records,
			startedByText,
			path: declaration.homePagePath,
			title: intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.NOTIFICATION_LINE}, {agentName, text}),
		};
	} catch (error) {
		reportOnce(params, () => Sentry.captureException(error));
		return describeFallback(startedByText, intl);
	}
};

export const AgentNotificationLine = ({row}: {row: AgentNotificationRow}): JSX.Element => {
	if (row.agentName === null) {
		return <>{row.text}</>;
	}
	return (
		<FormattedMessage
			id={AGENTIC_WORKERS_MESSAGE.NOTIFICATION_LINE}
			values={{
				agentName: <InlineText weight="medium">{row.agentName}</InlineText>,
				text: row.text,
			}}
		/>
	);
};

export const AgentNotificationDetails = ({row}: {row: AgentNotificationRow}): JSX.Element => {
	const [firstRecord, ...otherRecords] = row.records;
	return (
		<FlexRow gap="s" alignItems="center">
			{firstRecord !== undefined && (
				<WorkSignifier workId={firstRecord.label} name={firstRecord.name} color={firstRecord.color} />
			)}
			{otherRecords.length > 0 && (
				<Detail color="medium" noWrap title={otherRecords.map(record => record.label).join(OTHER_RECORDS_SEPARATOR)}>
					<FormattedMessage id={PLUS_MORE_MESSAGE} values={{count: otherRecords.length}} />
				</Detail>
			)}
			{row.startedByText !== null && (
				<Detail color="medium" noWrap>
					{row.startedByText}
				</Detail>
			)}
		</FlexRow>
	);
};
